import type { Currency } from "~/types/currencies";
import { Api } from "~/services/swagger/Api";
import {
  getLocalStorage,
  writePersistentLocalStorage,
} from "~/services/LocalStorage/localStorage";
import eventBus from "~/utilities/composables/eventBus";

const apiClient = new Api();

export const CURRENCIES: { [key: string]: Currency } = {
  USD: { code: "USD", symbol: "$", rate: 1, usdRate: 1 } as Currency,
} as const;

export const getCurrentCurrency = (key: string | null | undefined) => {
  if (!key) return CURRENCIES[BASE_CURRENCY.value.code];
  return CURRENCIES[key] || CURRENCIES[BASE_CURRENCY.value.code];
};

// Many currencies (CLP, ARS, MXN, AUD, CAD, …) use `$` as their native symbol.
// When such a value is shown alongside a USD `$` value (e.g. converted ≈ USD vs
// native price), the bare symbol becomes ambiguous. Prefix the 2-letter ISO
// code to produce `CL$`, `MX$`, `AU$`, etc.
export const getCurrencyDisplaySymbol = (
  currency: Currency | null | undefined
): string => {
  if (!currency) return CURRENCIES.USD.symbol;
  if (
    currency.symbol === CURRENCIES.USD.symbol &&
    currency.code !== CURRENCIES.USD.code
  ) {
    return `${currency.code.slice(0, 2)}${currency.symbol}`;
  }
  return currency.symbol;
};

export const setBaseCurrency = (key: string) => {
  if (CURRENCIES[key]) {
    baseCurrency.value = CURRENCIES[key];
  } else {
    console.warn(`Currency with key "${key}" not found. Falling back to USD.`);
    baseCurrency.value = CURRENCIES.USD;
  }
  writePersistentLocalStorage("user_currency", baseCurrency.value);
  eventBus.emit("change-currency");
};

const baseCurrency = ref<Currency>(CURRENCIES.USD);
export const BASE_CURRENCY = computed(() => baseCurrency.value);
export const HUGE_CURRENCY = ["IDR"];

export const USD_CURRENCY = CURRENCIES["USD"];

export const getAllCurrency = async () => {
  try {
    const response = await apiClient.api.currencyRatesControllerList();
    if (!response.ok) return;
    response.data.forEach((item) => {
      CURRENCIES[item.currency] = {
        code: item.currency,
        symbol: item.symbol,
        rate: parseFloat(item.rate.toString()),
        usdRate: parseFloat(item.inverseRate.toString()),
      };
    });

    const userCurrency: Currency | null | undefined =
      getLocalStorage("user_currency");
    if (userCurrency) {
      setBaseCurrency(userCurrency.code);
    }
  } catch (error) {
    console.log("error: ", error);
  }
};

getAllCurrency();
